/**
 * DIV.1 — Dividenden-Cache lesen.
 *
 * Schreib-Pfad ist der Cron `refresh-dividends.mts`. Frontend liest:
 *   - fetchDividendHistory(symbol)  — Detail-Card (letzte 8 Auszahlungen)
 *   - fetchDividendSummary(symbol)  — Watchlist-Indicator (1 Zeile)
 *   - fetchDividendSummaries(symbols) — Batch für Watchlist-Render
 */

import { supabase } from './supabase';

export interface DividendRow {
  symbol: string;
  ex_date: string;
  payment_date: string | null;
  amount: number;
  currency: string | null;
  frequency: string | null;
  yield_pct: number | null;
}

export interface DividendSummary {
  symbol: string;
  next_or_last_ex_date: string;
  next_or_last_payment_date: string | null;
  next_or_last_amount: number;
  currency: string | null;
  frequency: string | null;
  ttm_amount: number | null;
  ttm_count: number | null;
}

/**
 * Liefert die letzten N Dividenden eines Symbols (chronologisch absteigend),
 * inkl. der nächsten angekündigten Auszahlung wenn vorhanden.
 */
export async function fetchDividendHistory(
  symbol: string,
  limit: number = 8,
  signal?: AbortSignal
): Promise<DividendRow[]> {
  let q = supabase
    .from('v5_dividends_cache')
    .select('symbol, ex_date, payment_date, amount, currency, frequency, yield_pct')
    .eq('symbol', symbol.toUpperCase())
    .order('ex_date', { ascending: false })
    .limit(limit);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) {
    console.warn('[dividends] history fetch failed:', error.message);
    return [];
  }
  return (data ?? []) as DividendRow[];
}

/**
 * Eine Zeile pro Symbol mit aggregierten Kennzahlen — perfekt für die
 * Watchlist-Übersicht (mehrere Symbole gleichzeitig).
 */
export async function fetchDividendSummaries(
  symbols: string[],
  signal?: AbortSignal
): Promise<Map<string, DividendSummary>> {
  if (symbols.length === 0) return new Map();
  const upper = symbols.map((s) => s.toUpperCase());
  let q = supabase
    .from('v5_dividends_summary')
    .select('*')
    .in('symbol', upper);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) {
    console.warn('[dividends] summaries fetch failed:', error.message);
    return new Map();
  }
  const map = new Map<string, DividendSummary>();
  for (const row of (data ?? []) as DividendSummary[]) {
    map.set(row.symbol, row);
  }
  return map;
}

/** Detail-Card: 1 Symbol mit aggregierten Kennzahlen. */
export async function fetchDividendSummary(
  symbol: string,
  signal?: AbortSignal
): Promise<DividendSummary | null> {
  const map = await fetchDividendSummaries([symbol], signal);
  return map.get(symbol.toUpperCase()) ?? null;
}

/**
 * Liefert true, wenn die nächste Auszahlung in der Zukunft liegt — sonst
 * ist `next_or_last_*` die letzte vergangene Zahlung.
 */
export function isUpcoming(summary: DividendSummary): boolean {
  if (!summary.next_or_last_ex_date) return false;
  return new Date(summary.next_or_last_ex_date) > new Date();
}

/**
 * Tage bis zum nächsten ex-Date. Negativ wenn schon vergangen.
 */
export function daysToExDate(summary: DividendSummary): number | null {
  if (!summary.next_or_last_ex_date) return null;
  const now = new Date();
  const ex = new Date(summary.next_or_last_ex_date);
  return Math.round((ex.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
}
